import AccountBalanceWalletOutlined from '@material-ui/icons/AccountBalanceWalletOutlined'
import ChevronRight from '@material-ui/icons/ChevronRight'
import EmojiEventsOutlined from '@material-ui/icons/EmojiEventsOutlined'
import ExpandMore from '@material-ui/icons/ExpandMore'
import SupervisorAccountOutlined from '@material-ui/icons/SupervisorAccountOutlined'
import {Trans, withNamespaces, WithNamespaces} from 'react-i18next'
import messages from '../../../../assets/messages'
import {PammOfferType} from '../../../../graphql/types'
import {getOfferChainIds} from '../../utils/buildOfferTree'
import {formatKpiMoney} from '../../utils/kpiGrid'
import {offerDisplayName} from '../../utils/offerDisplayName'
import {InvestorListPopover} from '../InvestorListPopover'
import {
  Aum,
  ChevronButton,
  ChevronSpacer,
  HeaderLeft,
  Identity,
  LevelBadge,
  MetaItem,
  MetaRow,
  OfferName,
  RoleBadge,
  TitleRow,
} from './OfferCardHeader.styled'

type OfferCardHeaderProps = {
  offer: PammOfferType
  level: number
  hasChildren: boolean
  expanded: boolean
  onToggleExpand: () => void
} & WithNamespaces

/**
 * Identity block of an offer card: expand/collapse chevron, offer name, role and level
 * badges, and the meta row (account id, investor count, AUM).
 *
 * @param offer - The PAMM offer being rendered.
 * @param level - The offer's depth in the tree; shown in the level badge.
 * @param hasChildren - Whether to render the chevron button or an alignment spacer.
 * @param expanded - Current expand state; sets the chevron direction and aria-label.
 * @param onToggleExpand - Invoked when the chevron is clicked.
 * @param t - Translation function injected by `withNamespaces` (used for aria-labels).
 */
const _OfferCardHeader = ({offer, level, hasChildren, expanded, onToggleExpand, t}: OfferCardHeaderProps) => {
  const chainIds = getOfferChainIds(offer)
  const isManagerOffer = chainIds.length === 0

  // The Money Manager's offer is identified by its trading account login, an agent's by the
  // agent ID it owns the offer with (the first entry of the agent chain).
  const accountId = isManagerOffer ? offer.managerId : chainIds[0]

  return (
    <HeaderLeft>
      {hasChildren ? (
        <ChevronButton
          type="button"
          onClick={onToggleExpand}
          aria-expanded={expanded}
          aria-label={
            expanded
              ? t(messages.pammCollapseAria.i18nKey, messages.pammCollapseAria.defaults)
              : t(messages.pammExpandAria.i18nKey, messages.pammExpandAria.defaults)
          }
        >
          {expanded ? <ExpandMore style={{fontSize: 18}} /> : <ChevronRight style={{fontSize: 18}} />}
        </ChevronButton>
      ) : (
        <ChevronSpacer />
      )}

      <Identity>
        <TitleRow>
          <OfferName>{offerDisplayName(offer)}</OfferName>
          <RoleBadge>
            {isManagerOffer ? (
              <EmojiEventsOutlined style={{fontSize: 13}} />
            ) : (
              <SupervisorAccountOutlined style={{fontSize: 13}} />
            )}
            <Trans {...(isManagerOffer ? messages.pammRoleMoneyManager : messages.pammRoleAgent)} />
          </RoleBadge>
          <LevelBadge>
            <Trans {...messages.pammLevelBadge} values={{level}} />
          </LevelBadge>
        </TitleRow>

        <MetaRow>
          <span>
            {/* Agent cards show the agent account id from the offer's chain; the Money
                Manager card shows its trading account login. Label them accordingly. */}
            <Trans
              {...(isManagerOffer ? messages.pammOfferIdLabel : messages.pammAgentIdLabel)}
              values={{id: accountId}}
            />
          </span>
          <MetaItem>
            {/* Clicking the investor count opens the investor book anchored to this card. */}
            <InvestorListPopover offer={offer} />
          </MetaItem>
          <MetaItem>
            {/* Headline "Total Funds" figure: the offer's current AUM, rendered with the exact same
                formatter as the KPI panel's Total Funds tile so the two reconcile on a live
                cross-check (PT-5715). A missing AUM shows "–", never a misleading $0. */}
            <AccountBalanceWalletOutlined style={{fontSize: 14}} />
            <Aum data-testid="card-total-funds">{formatKpiMoney(offer.aum, offer.currency ?? 'USD')}</Aum>
          </MetaItem>
        </MetaRow>
      </Identity>
    </HeaderLeft>
  )
}

export const OfferCardHeader = withNamespaces()(_OfferCardHeader)
